import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { User, Customer } from '../types';
import { generateCustomerId } from '../utils/idGenerator';

export class AuthService {
  // Register a new user
  static async register(
    email: string,
    password: string,
    name: string,
    phone: string,
    address: string,
    city: string
  ): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      console.log('🚀 Starting user registration...', { email, name });
      
      // Create user with Firebase Auth
      console.log('📝 Creating Firebase user...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase user created:', firebaseUser.uid);

      // Update the user's display name
      console.log('👤 Updating user profile...');
      await updateProfile(firebaseUser, {
        displayName: name
      });
      console.log('✅ User profile updated');

      // Send email verification
      console.log('📧 Sending email verification...');
      await sendEmailVerification(firebaseUser);
      console.log('✅ Email verification sent');

      // Check if this is an admin email
      const adminEmails = [
        'sharam@gmail.com', 
        'admin@bookfood.com', 
        'akxay@gmail.com',
        'user@gmail.com'
        // Add more admin emails here
      ];
      const isAdmin = adminEmails.includes(firebaseUser.email!.toLowerCase());
      
      console.log('🔍 Checking admin status for email:', firebaseUser.email);
      console.log('🔍 Is admin email:', isAdmin);
      
      // Generate customer ID once for consistency
      const customerId = isAdmin ? undefined : generateCustomerId();
      console.log('🆔 Generated customer ID:', customerId);
      
      // Create user document in Firestore
      const userData: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email!,
        name: name,
        role: isAdmin ? 'admin' : 'customer',
        customerId: customerId,
        profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
      };

      // Save user data to Firestore
      console.log('💾 Saving user data to Firestore...');
      console.log('📊 User data to save:', JSON.stringify(userData, null, 2));
      await setDoc(doc(db, 'users', firebaseUser.uid), userData);
      console.log('✅ User document saved to Firestore with ID:', firebaseUser.uid);
      
      // Only create customer document for non-admin users
      if (!isAdmin) {
        const customerData: Customer = {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          name: name,
          phone,
          address,
          city,
          customerId: customerId!,
          profilePicture: userData.profilePicture,
          registrationDate: new Date().toISOString(),
          totalOrders: 0,
          totalSpent: 0
        };

        console.log('💾 Saving customer data to Firestore...');
        console.log('📊 Customer data to save:', JSON.stringify(customerData, null, 2));
        console.log('📊 Customer phone:', customerData.phone);
        console.log('📊 Customer address:', customerData.address);
        console.log('📊 Customer city:', customerData.city);
        console.log('📊 Customer ID:', customerData.customerId);
        await setDoc(doc(db, 'customers', firebaseUser.uid), customerData);
        console.log('✅ Customer document saved to Firestore with ID:', firebaseUser.uid);
        
        // Verify the document was saved
        const verifyDoc = await getDoc(doc(db, 'customers', firebaseUser.uid));
        console.log('🔍 Verification - Customer document exists:', verifyDoc.exists());
        if (verifyDoc.exists()) {
          console.log('✅ Verification - Customer document data:', verifyDoc.data());
        }
      } else {
        console.log('🔑 Admin user detected - skipping customer document creation');
      }
      
      console.log('🎉 Registration completed successfully!', { userId: firebaseUser.uid });
      return { success: true, user: userData };
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        email: email
      });
      return { 
        success: false, 
        error: error.message || 'Registration failed' 
      };
    }
  }

  // Login user
  static async login(email: string, password: string): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      console.log('🔐 Starting user login...', { email });
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase authentication successful:', firebaseUser.uid);

      // Get user data from Firestore
      console.log('📖 Fetching user data from Firestore...');
      console.log('🔍 Looking for user document with ID:', firebaseUser.uid);
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        console.log('✅ User data found in Firestore:');
        console.log('📊 User document data:', JSON.stringify(userData, null, 2));
        console.log('📊 User document ID:', userDoc.id);
        console.log('📊 User document exists:', userDoc.exists());
        return { success: true, user: userData };
      } else {
        console.log('⚠️ User document not found, creating user data...');
        console.log('🔍 User document exists:', userDoc.exists());
        console.log('🔍 User document ID:', userDoc.id);
        
        // Check if this is an admin email
        const adminEmails = [
          'sharam@gmail.com', 
          'admin@bookfood.com', 
          'akxay@gmail.com',
          'user@gmail.com'
          // Add more admin emails here
        ];
        const isAdmin = adminEmails.includes(firebaseUser.email!.toLowerCase());
        
        console.log('🔍 Checking admin status for email:', firebaseUser.email);
        console.log('🔍 Is admin email:', isAdmin);
        
        // Generate customer ID for consistency
        const customerId = isAdmin ? undefined : generateCustomerId();
        
        // Create user data with appropriate role
        const userData: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          name: firebaseUser.displayName || (isAdmin ? 'Admin User' : 'User'),
          role: isAdmin ? 'admin' : 'customer',
          ...(customerId && { customerId: customerId })
        };
        
        console.log('📊 Creating user data:', JSON.stringify(userData, null, 2));
        await setDoc(doc(db, 'users', firebaseUser.uid), userData);
        console.log('✅ User data created and saved with ID:', firebaseUser.uid);
        console.log('✅ User role:', userData.role);
        
        return { success: true, user: userData };
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        email: email
      });
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    }
  }

  // Logout user
  static async logout(): Promise<void> {
    try {
      console.log('🚪 Logging out user...');
      await signOut(auth);
      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    }
  }

  // Get current user
  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  // Listen to auth state changes
  static onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  // Reset password
  static async resetPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error: any) {
      console.error('Password reset error:', error);
      return { 
        success: false, 
        error: error.message || 'Password reset failed' 
      };
    }
  }

  // Update user profile
  static async updateUserProfile(userId: string, updates: Partial<User>): Promise<{ success: boolean; error?: string }> {
    try {
      await updateDoc(doc(db, 'users', userId), updates);
      return { success: true };
    } catch (error: any) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: error.message || 'Profile update failed' 
      };
    }
  }

  // Change password
  static async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        return { success: false, error: 'No user logged in' };
      }

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);
      
      console.log('✅ Password updated successfully');
      return { success: true };
    } catch (error: any) {
      console.error('Password change error:', error);
      let errorMessage = 'Password change failed';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log in again before changing your password';
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  }
}
